import { makeAutoObservable, observable, action, runInAction } from 'mobx';
import { checkPaymentCall, checkRenewPaymentCall } from '../api/orderService';
import { PaidOrderDto, PaidRenewOrderDto } from '../models/dtoTypes';
import { RootStore } from './rootStore';

export default class OrderStore {
    rootStore: RootStore | undefined;
    @observable order: PaidOrderDto  | PaidRenewOrderDto | undefined = undefined;
    @observable error: string | null = null;
    @observable loading = false;



    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
        makeAutoObservable(this);
    }

    // Some payments settle a few seconds after the checkout closes,
    // so the server can briefly report the payment as pending - poll until it settles
    static readonly POLL_ATTEMPTS = 20;
    static readonly POLL_INTERVAL_MS = 3000;
    static readonly STILL_PENDING_MESSAGE =
        'Your payment is still being processed. Please refresh this page in a minute - the confirmation will also arrive by email.';

    @action
    async checkPayment(paymentId: number) {
        if (this.order && this.order !== null) {
            if (this.order?.paymentId === paymentId)
                return;
        }
        if (this.loading) return;

        runInAction(() => {
            this.loading = true;
        });

        try {
            for (let attempt = 0; attempt < OrderStore.POLL_ATTEMPTS; attempt++) {
                const paymentStatus = await checkPaymentCall(paymentId);

                if (!paymentStatus.pending) {
                    runInAction(() => {
                        // Update the paymentStatus in the store
                        this.order = paymentStatus;
                    });
                    break;
                }
                await new Promise(resolve => setTimeout(resolve, OrderStore.POLL_INTERVAL_MS));
            }
            if (this.order === undefined) {
                runInAction(() => {
                    this.order = { error: OrderStore.STILL_PENDING_MESSAGE } as PaidOrderDto;
                });
            }
        } catch (error) {
            // Handle any errors that occur during the payment check
            console.error('Error checking payment:', error);
        }
        runInAction(() => {
            this.loading = false;
        });
    }

    @action
    async checkRenewPayment(paymentId: number) {
        if (this.order && this.order !== null) {
            if (this.order?.paymentId === paymentId) 
                return;
        }
        if (this.loading) return;

        runInAction(() => {
            this.loading = true;
        });

        try {
            for (let attempt = 0; attempt < OrderStore.POLL_ATTEMPTS; attempt++) {
                const paymentStatus = await checkRenewPaymentCall(paymentId);

                if (!paymentStatus.pending) {
                    runInAction(() => {
                        // Update the paymentStatus in the store
                        this.order = paymentStatus;
                    });
                    break;
                }
                await new Promise(resolve => setTimeout(resolve, OrderStore.POLL_INTERVAL_MS));
            }
            if (this.order === undefined) {
                runInAction(() => {
                    this.order = { error: OrderStore.STILL_PENDING_MESSAGE } as PaidRenewOrderDto;
                });
            }
        } catch (error) {
            // Handle any errors that occur during the payment check
            console.error('Error checking renew payment:', error);
        }
        runInAction(() => {
            this.loading = false;
        });
    }
}
